import React, { useCallback, useContext } from 'react';
import MediaQuery from 'react-responsive';
import {
  AnalysisViewContext,
  BasicTable,
  type BasicTableColumn,
  type BasicTableProps,
  UserContext,
  useShouldUseNetAssetsInsteadOfAllocation,
} from 'venn-components';
import {
  type AnalysisConfig,
  calculateComparison,
  formatAllocation,
  getFlattenedNodesWithCompareAndLevel,
  getName,
  isOptionNode,
  normalizePortfolio,
  type PortfolioWithLevel,
  useHasFF,
} from 'venn-utils';
import styled, { ThemeContext } from 'styled-components';
import { compact, isNil } from 'lodash';
import type { Portfolio } from 'venn-api';
import { PrintAnalysisTemplate } from 'venn-ui-kit';
import { getComparisonLabel } from '../workspace/exportData/helper';
import { OptionIcon } from '../../../../../../venn-components/src/common/OptionIcon';

interface PortfolioNavsProps {
  analysisConfig: AnalysisConfig;
}

const PortfolioNavs = ({ analysisConfig }: PortfolioNavsProps) => {
  const strategy = analysisConfig.subject?.strategy;
  const hasExportsDisabled = useHasFF('disable_exports_ff');

  if (!strategy || strategy.fund || hasExportsDisabled || analysisConfig.subject?.isHistoricalPortfolio) {
    return null;
  }

  return (
    <MediaQuery print>
      {(print) =>
        print && (
          <div>
            <PrintAnalysisTemplate>Portfolio NAVs</PrintAnalysisTemplate>
            <PortfolioNavsTable
              strategy={strategy}
              secondary={analysisConfig.subject?.secondaryPortfolio}
              secondaryLabel={getComparisonLabel(analysisConfig.subject)}
            />
          </div>
        )
      }
    </MediaQuery>
  );
};

export default PortfolioNavs;

interface PortfolioNavsTableProps {
  strategy: Portfolio;
  secondary?: Portfolio;
  secondaryLabel?: string;
}

const PortfolioNavsTable = ({ strategy, secondary, secondaryLabel }: PortfolioNavsTableProps) => {
  const { isPercentageMode } = useContext(AnalysisViewContext);
  const userContext = useContext(UserContext);
  const { Colors } = useContext(ThemeContext);
  const workspaceCurrency = userContext.profileSettings?.organization?.currency;
  const shouldUseNetAssetsInsteadOfAllocation = useShouldUseNetAssetsInsteadOfAllocation();

  const getAllocation = useCallback(
    (portfolio?: Portfolio) => {
      if (shouldUseNetAssetsInsteadOfAllocation) {
        return portfolio?.netAssets;
      }
      return portfolio?.allocation;
    },
    [shouldUseNetAssetsInsteadOfAllocation],
  );

  const normalizedStrategy = normalizePortfolio(strategy);
  const normalizedSecondary = isNil(secondary) ? undefined : normalizePortfolio(secondary);
  const [allCompareNodes, allGhostChildren] = calculateComparison(normalizedStrategy, normalizedSecondary);

  const nodes = getFlattenedNodesWithCompareAndLevel(
    normalizedStrategy,
    normalizedSecondary,
    allCompareNodes,
    allGhostChildren,
  );

  const rootAllocation = getAllocation(nodes[0]?.[0]);

  const investmentCellRenderer = useCallback(
    (data: [PortfolioWithLevel | undefined, PortfolioWithLevel | undefined]) => {
      const [node, compare] = [data[0], data[1]];
      const name = getName(node, compare);
      const optionData = isOptionNode(node ?? compare) ? (node ?? compare)?.fund?.optionData : undefined;
      return (
        <div
          style={{
            marginLeft: `${20 * (node?.level ?? compare?.level ?? 0)}px`,
            fontWeight: isNil(node?.fund ?? compare?.fund) && node?.designation !== 'CASH' ? 'bold' : 'normal',
            color: isNil(node) ? Colors.GreyScale.Grey60 : Colors.Black,
          }}
        >
          {name} {optionData && <OptionIcon optionType={optionData.optionType} />}
        </div>
      );
    },
    [Colors],
  );

  const allocationCellRenderer = useCallback(
    (data: [PortfolioWithLevel | undefined, PortfolioWithLevel | undefined]) => {
      const [node, compare] = [data[0], data[1]];
      return (
        <div
          style={{ fontWeight: isNil(node?.fund ?? compare?.fund) && node?.designation !== 'CASH' ? 'bold' : 'normal' }}
        >
          {!isNil(node)
            ? formatAllocation(
                getAllocation(node) ?? 0,
                false,
                !!(isPercentageMode && rootAllocation && getAllocation(node)),
                rootAllocation,
              )
            : '--'}
        </div>
      );
    },
    [getAllocation, isPercentageMode, rootAllocation],
  );

  const allocationPercentageCellRenderer = useCallback(
    (data: [PortfolioWithLevel | undefined, PortfolioWithLevel | undefined]) => {
      const [node, compare] = [data[0], data[1]];
      return (
        <div style={{ fontWeight: isNil(node?.fund ?? compare?.fund) ? 'bold' : 'normal' }}>
          {!isNil(compare)
            ? formatAllocation(
                getAllocation(compare) ?? 0,
                false,
                !!(isPercentageMode && rootAllocation && getAllocation(compare)),
                rootAllocation,
              )
            : '--'}
        </div>
      );
    },
    [getAllocation, isPercentageMode, rootAllocation],
  );

  const columns = compact([
    {
      label: 'Investment',
      sortable: false,
      cellRenderer: investmentCellRenderer,
    },
    {
      label: `Allocation (${isPercentageMode ? '%' : workspaceCurrency})`,
      sortable: false,
      cellRenderer: allocationCellRenderer,
    },
    isNil(secondary)
      ? null
      : {
          label: `${secondaryLabel} Allocation (${isPercentageMode ? '%' : workspaceCurrency})`,
          sortable: false,
          cellRenderer: allocationPercentageCellRenderer,
        },
  ]);
  return <StyledBasicTable data={nodes} columns={columns} />;
};

const StyledBasicTable = styled(<T extends BasicTableColumn<K>, K>(props: BasicTableProps<T, K>) => (
  <BasicTable<T, K> {...props} />
))`
  margin: 10px 0 30px;
`;
